import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useServerHealth } from '../hooks/useServerHealth';
import { AuthPageLayout, AuthForm } from '../components/layout';
import { pageStyles } from '../config/theme';
import useGameStore from '../stores/gameStore';
import useAuthStore from '../stores/authStore';
import LoadingPage from '../components/ui/LoadingPage';
import guestService from '../services/guestService';
import { parseInviteLinkFromSearch } from '../utils/inviteLink';
import type { ApiError } from '../types';

const HomePage: React.FC = () => {
  const { isSessionRestored, isGuest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loadSystemSession = useGameStore((state) => state.loadSystemSession);
  const createGuestSession = useGameStore((state) => state.createGuestSession);
  const [isLoadingSystemSession, setIsLoadingSystemSession] = useState(false);
  const [hasConnectionError, setHasConnectionError] = useState(false);
  const [nonRetryableErrorCode, setNonRetryableErrorCode] = useState<string | null>(null);
  const hasAttemptedRedirect = useRef(false);
  const prevPathRef = useRef<string>(location.pathname);

  const NON_RETRYABLE_ERROR_CODES = ['ADVENTURE_NOT_FOUND'];
  
  const { health, backendUrl, imageServerUrl, systemAdventureId } = useServerHealth();
  
  // Get current hostname and protocol for health endpoint links
  // This ensures links work when accessing from phone/network IP
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  const currentProtocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  
  // Extract ports from URLs for health endpoint links
  const getBackendPort = (url: string): string => {
    const match = url.match(/:(\d+)/);
    return match?.[1] ?? '3031';
  };
  
  const getImageServerPort = (url: string): string => {
    const match = url.match(/:(\d+)/);
    return match?.[1] ?? '3032';
  };
  
  const backendHealthUrl = `${currentProtocol}//${currentHost}:${getBackendPort(backendUrl)}/health`;
  const imageServerHealthUrl = `${currentProtocol}//${currentHost}:${getImageServerPort(imageServerUrl)}/health`;

  // Extract navigation function so it can be reused
  const navigateToSystemAdventure = React.useCallback(async (urlSearch?: string) => {
    // Check if we've already attempted redirect
    if (hasAttemptedRedirect.current) {
      return;
    }
    
    setIsLoadingSystemSession(true);
    // Set flag early to prevent multiple attempts
    hasAttemptedRedirect.current = true;

    try {
      // Wait a bit for auth state to be fully restored after navigation
      // This ensures we have the correct isAuthenticated and isGuest values
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Re-check auth state to ensure it's up to date
      const currentAuthState = useAuthStore.getState();
      const isCurrentlyAuthenticated = currentAuthState.isAuthenticated;
      const isCurrentlyGuest = currentAuthState.isGuest;
      
      console.log('[HomePage] Redirect check:', { 
        isAuthenticated: isCurrentlyAuthenticated, 
        isGuest: isCurrentlyGuest,
        isSessionRestored: currentAuthState.isSessionRestored 
      });
      
      let result;
      
      if (isCurrentlyAuthenticated && !isCurrentlyGuest) {
        // Authenticated (non-guest) users: load existing system session
        // Parse invite link and pass join-target vars when present
        const searchToUse = urlSearch ?? (typeof window !== 'undefined' ? window.location.search : '');
        const { linkVariables } = parseInviteLinkFromSearch(searchToUse);
        const hasJoinTargetVars =
          linkVariables &&
          (linkVariables['user_invite_session_id'] !== undefined ||
            linkVariables['user_invite_player_slot'] !== undefined);
        const inviteVars = hasJoinTargetVars ? linkVariables : undefined;
        console.log('[HomePage] Loading system session for authenticated user', { hasInviteVars: !!inviteVars });
        result = await loadSystemSession(inviteVars);
      } else {
        // Unauthenticated users or guest users: create guest session for the system adventure.
        // Use URL search from when the effect ran (urlSearch) so all invite params are captured.
        let adventureIdToUse = systemAdventureId;
        try {
          const healthRes = await fetch(`${backendUrl}/health`);
          if (healthRes.ok) {
            const data = await healthRes.json();
            if (typeof data.systemAdventureId === 'string' && data.systemAdventureId.trim()) {
              adventureIdToUse = data.systemAdventureId.trim();
              useGameStore.getState().setSystemAdventureId(adventureIdToUse);
            }
          }
        } catch (e) {
          console.warn('[HomePage] Could not fetch system adventure ID from backend, using current value:', adventureIdToUse);
        }
        const { linkVariables } = parseInviteLinkFromSearch(urlSearch ?? (typeof window !== 'undefined' ? window.location.search : ''));
        const invitePayload =
          linkVariables && Object.keys(linkVariables).length > 0
            ? { variables: linkVariables }
            : undefined;
        console.log('[HomePage] Creating guest session', {
          isAuthenticated: isCurrentlyAuthenticated,
          isGuest: isCurrentlyGuest,
          adventureId: adventureIdToUse,
          hasGuestData: !!guestService.getGuestData(),
          hasLinkVariables: !!invitePayload,
        });
        try {
          result = await createGuestSession(adventureIdToUse, invitePayload);
          console.log('[HomePage] Guest session created successfully', result);
        } catch (guestError) {
          console.error('[HomePage] Failed to create guest session:', guestError);
          // Re-throw to be handled by outer catch
          throw guestError;
        }
      }

      const { sessionId, playerId } = result || {};

      if (sessionId && playerId) {
        // For guest users, ensure auth state is set before navigation
        // This ensures ProtectedRoute will allow access
        const authState = useAuthStore.getState();
        
        if (authState.isGuest && !authState.isAuthenticated) {
          // Wait a bit more for state to propagate
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Check again
          const updatedAuthState = useAuthStore.getState();
          if (updatedAuthState.isGuest && !updatedAuthState.isAuthenticated) {
            throw new Error('Guest user authentication failed');
          }
        }
        
        // Navigate using React Router
        try {
          navigate(`/scene/${sessionId}/${playerId}`);
        } catch (navError) {
          // Fallback to window.location
          window.location.href = `/scene/${sessionId}/${playerId}`;
        }
      } else {
        // Reset flag if redirect failed
        hasAttemptedRedirect.current = false;
        throw new Error(`Failed to get sessionId or playerId: sessionId=${sessionId}, playerId=${playerId}`);
      }
    } catch (error) {
      // Log error for debugging
      console.error('[HomePage] Failed to navigate to system adventure:', error);

      // Stale guest (e.g. backend restarted): clear guest data and retry once instead of showing connection error
      const apiErr = error as ApiError;
      if (apiErr?.code === 'USER_NOT_FOUND') {
        useAuthStore.getState().clearGuestData();
        hasAttemptedRedirect.current = false;
        setIsLoadingSystemSession(false);
        return navigateToSystemAdventure(location.search);
      }

      // Guest was converted to registered user (e.g. after registration): clear stale guest state and retry
      // Without this, we'd show connection error and retry forever since createGuestSession keeps failing
      if (
        apiErr?.code === 'VALIDATION_ERROR' &&
        typeof apiErr?.message === 'string' &&
        apiErr.message.includes('not a guest user')
      ) {
        console.log('[HomePage] Guest was converted to registered user, clearing stale guest state and retrying');
        useAuthStore.getState().clearGuestData();
        hasAttemptedRedirect.current = false;
        setIsLoadingSystemSession(false);
        return navigateToSystemAdventure(location.search);
      }

      // Non-retryable errors (e.g. ADVENTURE_NOT_FOUND): show error, do not retry automatically
      if (apiErr?.code && NON_RETRYABLE_ERROR_CODES.includes(apiErr.code)) {
        setNonRetryableErrorCode(apiErr.code);
        setHasConnectionError(true);
        setIsLoadingSystemSession(false);
        return;
      }

      // Transient/connection errors: show error and allow retry when both servers healthy
      setNonRetryableErrorCode(null);
      setHasConnectionError(true);
      hasAttemptedRedirect.current = false;
      setIsLoadingSystemSession(false);
      throw error; // Re-throw so caller knows it failed
    } finally {
      setIsLoadingSystemSession(false);
    }
  }, [navigate, loadSystemSession, createGuestSession, systemAdventureId, backendUrl]);

  // Auto-retry redirect when servers come back online (skip for non-retryable errors)
  useEffect(() => {
    if (!hasConnectionError || nonRetryableErrorCode) {
      return; // Only retry if we're in error state and error is retryable
    }

    // Check if both servers are healthy
    const bothHealthy = health.backend.healthy && health.imageServer.healthy;
    
    if (bothHealthy && !hasAttemptedRedirect.current && !isLoadingSystemSession) {
      console.log('[HomePage] Both servers are healthy, retrying redirect...');
      // Reset the error flag and trigger redirect
      setHasConnectionError(false);
      navigateToSystemAdventure(location.search).catch((error) => {
        // If redirect fails again, show error page
        console.error('[HomePage] Retry redirect failed:', error);
        setHasConnectionError(true);
      });
    }
  }, [health.backend.healthy, health.imageServer.healthy, hasConnectionError, nonRetryableErrorCode, isLoadingSystemSession, navigateToSystemAdventure, location.search]);

  // Auto-redirect authenticated users to system adventure on initial load
  // Wait for at least one backend health check so systemAdventureId is from the server
  // (avoids sending default 'storynity' when backend uses an alternate system adventure e.g. 'nexus')
  useEffect(() => {
    if (!isSessionRestored) {
      return;
    }

    // Wait for first backend health response so systemAdventureId is populated from /health
    if (!health.backend.lastChecked) {
      return;
    }

    // Skip auto-redirect if we came from the login page
    // This prevents creating a guest session when user navigates from login page
    // (e.g., if they manually navigate or if there's some other redirect)
    const fromLogin = (location.state as any)?.fromLogin || 
                      document.referrer.includes('/auth/login');
    
    if (fromLogin) {
      console.log('[HomePage] Skipping auto-redirect - came from login page');
      return;
    }

    // Only reset redirect flag and error state when user navigated to home from another page
    // This prevents duplicate createGuestSession when the effect re-runs and allows a fresh attempt
    if (prevPathRef.current !== '/' && location.pathname === '/') {
      hasAttemptedRedirect.current = false;
      setNonRetryableErrorCode(null);
      setHasConnectionError(false);
    }
    prevPathRef.current = location.pathname;

    // Skip if we already attempted redirect this "visit" to avoid duplicate guest session creation
    if (hasAttemptedRedirect.current) {
      return;
    }

    navigateToSystemAdventure(location.search).catch((error) => {
      // Error handling is done in navigateToSystemAdventure
      console.error('[HomePage] Initial redirect failed:', error);
    });
  }, [isSessionRestored, isGuest, health.backend.lastChecked, navigateToSystemAdventure, location.search, location.state]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // No form submission needed for homepage
  };

  // Show loading while redirecting users (authenticated or guest)
  if (isLoadingSystemSession) {
    return <LoadingPage title="Loading Nexus..." subtitle="Preparing your adventure hub" />;
  }

  // Only show error page if there's a connection error
  // Otherwise, the page should redirect automatically
  if (!hasConnectionError) {
    return null; // Will redirect, so show nothing
  }

  // Format last checked time
  const formatLastChecked = (date: Date | null): string => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  };

  return (
    <AuthPageLayout
      title="Storynity"
      subtitle=""
    >
      <AuthForm onSubmit={handleSubmit}>
        <div className="px-6 py-4">
          <div className="mb-6">
            <p style={{ ...pageStyles.subtitle, fontStyle: 'italic' }}>
              {nonRetryableErrorCode === 'ADVENTURE_NOT_FOUND'
                ? "The system adventure isn't available on this server. Please try again later or contact support."
                : 'Sorry, it seems we are having technical issues...'}
            </p>
          </div>

          {/* Backend Server Status */}
          <div className="mb-6">
            <div
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 style={{ color: '#ffffff', fontSize: '1.125rem', fontWeight: '600' }}>
                  Backend Server
                </h3>
                <span
                  style={{
                    color: health.backend.healthy ? '#10b981' : '#ef4444',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {health.backend.healthy ? '✓ Online' : '✗ Offline'}
                </span>
              </div>
              
              {health.backend.error && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {health.backend.error}
                </p>
              )}
              
              {health.backend.lastChecked && (
                <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  Last checked: {formatLastChecked(health.backend.lastChecked)}
                </p>
              )}
              
              <a
                href={backendHealthUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#60a5fa',
                  fontSize: '0.875rem',
                  textDecoration: 'underline',
                  marginTop: '0.5rem',
                  display: 'inline-block'
                }}
              >
                View health endpoint →
              </a>
            </div>
          </div>

          {/* Image Server Status */}
          <div className="mb-6">
            <div
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 style={{ color: '#ffffff', fontSize: '1.125rem', fontWeight: '600' }}>
                  Image Server
                </h3>
                <span
                  style={{
                    color: health.imageServer.healthy ? '#10b981' : '#ef4444',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {health.imageServer.healthy ? '✓ Online' : '✗ Offline'}
                </span>
              </div>
              
              {health.imageServer.error && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {health.imageServer.error}
                </p>
              )}
              
              {health.imageServer.lastChecked && (
                <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  Last checked: {formatLastChecked(health.imageServer.lastChecked)}
                </p>
              )}
              
              <a
                href={imageServerHealthUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#60a5fa',
                  fontSize: '0.875rem',
                  textDecoration: 'underline',
                  marginTop: '0.5rem',
                  display: 'inline-block'
                }}
              >
                View health endpoint →
              </a>
            </div>
          </div>
        </div>
      </AuthForm>
    </AuthPageLayout>
  );
};

export default HomePage;
